// Route: /recordings/[id] — детальная запись «Картотека»: тактильный плеер +
// транскрипт с таймкодами и спикерами + CTA «конспект лекции».
//
// Phase 4 рефактор: реальный <audio> с src из `recordings.getPlaybackUrl`
// (presigned GET). onTimeUpdate → position, play/pause/seek через ref.
// Тактильный редизайн: плеер — elevated-panel e2 с крупной тактильной клавишей
// play, утопленным well-scrubber'ом, клавишами перемотки ±15с. Транскрипт —
// карточки-реплики (e0/e1) с моно-таймкодами и спикер-чипами.

import {
	Button,
	cn,
	ElevatedPanel,
	EntityDetailHeader,
	ErrorState,
	PANEL_PAD,
	PANEL_PAD_CHROME,
	Skeleton,
	Slider,
} from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import {
	IconAlertTriangle,
	IconArrowLeft,
	IconFileText,
	IconLoader2,
	IconNotebook,
	IconPlayerPauseFilled,
	IconPlayerPlay,
	IconPlayerPlayFilled,
	IconPlayerStopFilled,
	IconPlayerTrackNextFilled,
	IconPlayerTrackPrevFilled,
	IconRefresh,
	IconSparkles,
	IconTrash,
	IconWaveSine,
} from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { useGenerationWait } from "@/hooks/use-generation-wait";
import type { LocalNote } from "@/hooks/use-local-materials";
import { markArtifactSeenKey, useMarkArtifactSeen } from "@/hooks/use-mark-artifact-seen";
import { useMaterialNotesGeneration } from "@/hooks/use-material-notes-generation";
import { useSocketEvent } from "@/hooks/use-socket-event";
import { trackGenerationAccepted } from "@/lib/ecosystem-analytics";
import { startPlayback } from "@/lib/play-media";
import { toastError } from "@/lib/user-facing-error";

const MarkdownLatex = lazy(() => import("@/components/visual-explainer/markdown-latex"));

import {
	type RecordingDetailDto,
	type TranscriptSegmentDto,
	useRecording,
} from "@/components/recordings/_api";
import { formatDurationHms } from "@/components/recordings/_format";
import { DetailHeader } from "@/components/recordings/detail-header";
import { ProcessingLoader } from "@/components/recordings/processing-loader";
import { RecordingDocView } from "@/components/recordings/recording-doc-view";
import { RecordingExportMenu } from "@/components/recordings/recording-export-menu";
import { RecordingLiveScreen } from "@/components/recordings/recording-live-screen";
import { SegmentedTabs } from "@/components/recordings/segmented-tabs";
import { TimecodeChip } from "@/components/recordings/timecode-chip";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { readPreferredMic } from "@/hooks/use-audio-input-devices";
import { CHUNK_SEC } from "@/hooks/use-chunk-upload-queue";
import { api } from "@/lib/trpc-client";
import { useRecorderHandle } from "@/providers/recorder-provider";
import { useRecorderStore } from "@/stores/recorder";

interface NoteCardProps {
	recordingId: string;
	savedMaterialId: string | null;
	canGenerate: boolean;
}

/**
 * RecordingNoteCard — слева под плеером: либо CTA «Создать конспект», либо
 * мини-карточки уже созданных note (как ArtifactCard в чате) с «Открыть».
 * Источник списка — PowerSync + tRPC overlay (`useMaterialNotesGeneration`).
 */
function RecordingNoteCard({
	recordingId,
	savedMaterialId,
	canGenerate,
}: NoteCardProps): React.ReactElement {
	const { t } = useTranslation("recordings");
	const navigate = useNavigate();
	const utils = api.useUtils();
	const [createdMaterialId, setCreatedMaterialId] = useState<string | null>(null);
	const [pendingIds, setPendingIds] = useState<string[]>([]);
	const pendingJobIdRef = useRef<string | null>(null);
	const materialId = savedMaterialId ?? createdMaterialId;
	const materialIdRef = useRef(materialId);
	materialIdRef.current = materialId;
	const { items, readyNotes, generatingCount, invalidateRemote } = useMaterialNotesGeneration(
		materialId,
		pendingIds,
	);
	const readyNotesRef = useRef(readyNotes);
	readyNotesRef.current = readyNotes;
	const serverPending = generatingCount > 0 || pendingIds.length > 0;
	const serverPendingRef = useRef(serverPending);
	serverPendingRef.current = serverPending;

	const wait = useGenerationWait({
		serverPending,
		readyEvent: "note:ready",
		matchReady: (raw) => {
			const payload = raw as { noteId?: string; materialId?: string | null; jobId?: string };
			const target = materialIdRef.current;
			const pendingJob = pendingJobIdRef.current;
			const id = payload.noteId ?? payload.jobId;
			if (pendingJob && (payload.noteId === pendingJob || payload.jobId === pendingJob)) {
				return true;
			}
			if (payload.materialId && target && payload.materialId === target) return true;
			return Boolean(id && pendingIds.includes(id));
		},
		failedEvent: "note:generation_failed",
		matchFailed: (raw) => {
			const payload = raw as { jobId?: string; materialId?: string | null };
			const pendingJob = pendingJobIdRef.current;
			if (pendingJob) return !payload.jobId || payload.jobId === pendingJob;
			if (!payload.jobId) return false;
			const target = materialIdRef.current;
			if (payload.materialId && target && payload.materialId !== target) return false;
			return serverPendingRef.current;
		},
		onPoll: () => {
			invalidateRemote();
			void utils.recordings.getById.invalidate({ recordingId });
		},
		pollIntervalMs: 4_000,
		timeoutMs: 120_000,
		onReady: (raw) => {
			pendingJobIdRef.current = null;
			setPendingIds([]);
			const payload = raw as { noteId: string };
			toast.success(t("detail.note.ready"), {
				action: {
					label: t("detail.note.open"),
					onClick: () =>
						void navigate({
							to: "/notes/$noteId",
							params: { noteId: payload.noteId },
						}),
				},
			});
		},
		onPendingCleared: () => {
			const pending = pendingJobIdRef.current;
			pendingJobIdRef.current = null;
			setPendingIds([]);
			const ready = pending
				? readyNotesRef.current.find((n) => n.id === pending)
				: readyNotesRef.current[0];
			if (!ready) return;
			toast.success(t("detail.note.ready"), {
				action: {
					label: t("detail.note.open"),
					onClick: () =>
						void navigate({
							to: "/notes/$noteId",
							params: { noteId: ready.id },
						}),
				},
			});
		},
		onFailed: () => {
			pendingJobIdRef.current = null;
			setPendingIds([]);
			toast.error(t("detail.note.failed"));
		},
		onTimeout: () => {
			pendingJobIdRef.current = null;
			setPendingIds([]);
			toast.error(t("detail.note.failed"));
		},
	});

	const generateNote = api.recordings.generateNote.useMutation({
		onMutate: () => wait.request(),
		onSuccess: (data) => {
			trackGenerationAccepted("summary", data);
			if (data.materialId) setCreatedMaterialId(data.materialId);
			if (data.jobId) {
				pendingJobIdRef.current = data.jobId;
				setPendingIds((prev) => (prev.includes(data.jobId) ? prev : [...prev, data.jobId]));
			}
			toast.success(t("detail.note.queuedToast"));
			void utils.recordings.getById.invalidate({ recordingId });
			// reused = material уже был — новая note всё равно генерируется;
			// остаёмся на странице и ждём note:ready (раньше уводили в /materials).
		},
		onError: (err) => {
			pendingJobIdRef.current = null;
			setPendingIds([]);
			wait.reset();
			toastError(err, t("detail.note.failed"), t);
		},
	});

	useEffect(() => {
		setPendingIds((prev) => {
			const next = prev.filter((id) => !items.some((n) => n.id === id));
			return next.length === prev.length ? prev : next;
		});
	}, [items]);

	const isPending = generateNote.isPending;
	const hasNotes = readyNotes.length > 0;

	// Первая генерация без списка — полноэкранный waiting. Если конспекты уже
	// есть, pending виден на мини-карточке (спиннер), общий блок не прячем.
	if (wait.isWaiting && !hasNotes) {
		return (
			<ElevatedPanel
				elevation="e1"
				className={cn(PANEL_PAD_CHROME, "flex items-center gap-3 border-primary/20")}
			>
				<span className="grid size-9 shrink-0 place-items-center rounded-[var(--radius-tile)] bg-[var(--c-ai-bg)] text-[var(--c-ai-fg)] [box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)]">
					<IconLoader2 size={18} className="animate-spin" />
				</span>
				<div className="min-w-0 flex-1">
					<div className="font-medium text-foreground">{t("detail.note.title")}</div>
					<p className="text-sm text-muted-foreground">{t("detail.note.creating")}</p>
				</div>
			</ElevatedPanel>
		);
	}

	if (hasNotes || generatingCount > 0) {
		const miniNotes =
			readyNotes.length > 0
				? readyNotes
				: items.filter((n) => n.generation_status === "generating");
		return (
			<div className="flex flex-col gap-2">
				<div className="px-0.5 text-sm font-medium text-foreground">{t("detail.note.title")}</div>
				{miniNotes.map((note) => (
					<RecordingNoteMiniCard key={note.id} note={note} />
				))}
			</div>
		);
	}

	return (
		<ElevatedPanel elevation="e1" className={cn(PANEL_PAD, "space-y-3")}>
			<div className="flex items-start gap-3">
				<span className="grid size-10 shrink-0 place-items-center rounded-[11px] bg-[var(--c-ai-bg)] text-[var(--c-ai-fg)] [box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)]">
					<IconSparkles size={20} />
				</span>
				<div className="min-w-0 flex-1">
					<div className="font-semibold text-foreground">{t("detail.note.title")}</div>
					<p className="mt-1 text-sm text-muted-foreground">{t("detail.note.emptyHint")}</p>
				</div>
			</div>
			<Button
				className="cursor-pointer"
				disabled={!canGenerate || isPending}
				onClick={() => generateNote.mutate({ recordingId })}
			>
				{isPending ? (
					<>
						<IconLoader2 size={16} className="mr-2 animate-spin" />
						{t("detail.note.creating")}
					</>
				) : (
					<>
						<IconSparkles size={16} className="mr-2" />
						{t("detail.note.create")}
					</>
				)}
			</Button>
		</ElevatedPanel>
	);
}

/** Мини-карточка note — визуально как ArtifactCard в чате (иконка + title + Открыть). */
function RecordingNoteMiniCard({ note }: { note: LocalNote }): React.ReactElement {
	const { t } = useTranslation("recordings");
	const status = note.generation_status ?? "ready";
	const isGenerating = status === "generating";
	const isFailed = status === "failed";
	const isReady = !isGenerating && !isFailed;
	const title = note.title?.trim() || t("detail.note.title");

	return (
		<div
			className="flex w-full items-center gap-3 rounded-[var(--radius-card)] border border-border bg-card px-3 py-2.5 shadow-e1"
			data-note-card={note.id}
			data-status={status}
		>
			<div
				className={cn(
					"inline-flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-tile)]",
					isFailed ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary",
				)}
			>
				{isGenerating ? (
					<IconLoader2 size={18} className="animate-spin" aria-hidden />
				) : isFailed ? (
					<IconAlertTriangle size={18} aria-hidden />
				) : (
					<IconNotebook size={18} aria-hidden />
				)}
			</div>
			<div className="min-w-0 flex-1">
				<p className="truncate text-sm font-medium text-foreground">{title}</p>
				<p className="mt-0.5 truncate text-xs text-muted-foreground">
					{isGenerating
						? t("detail.note.creating")
						: isFailed
							? t("detail.note.failed")
							: t("detail.note.version", { version: note.version })}
				</p>
			</div>
			{isReady ? (
				<Button asChild size="sm" variant="secondary" className="h-9 shrink-0 cursor-pointer">
					<Link to="/notes/$noteId" params={{ noteId: note.id }}>
						{t("detail.note.open")}
					</Link>
				</Button>
			) : null}
		</div>
	);
}

/**
 * StalledRecordingView — экран прерванной (recording/paused) или неудавшейся
 * (error) записи. Вместо сломанного плеера (duration=0) показывает понятное
 * состояние + действия: «Продолжить» (continueExisting → та же lecture_session),
 * «Завершить и обработать» (finalize → processRecording) или «Удалить».
 * Распознанный транскрипт (live-STT) показывается readonly.
 */
function StalledRecordingView({
	recording,
	transcript,
	mode,
}: {
	recording: RecordingDetailDto;
	transcript: TranscriptSegmentDto[];
	mode: "stalled" | "failed";
}): React.ReactElement {
	const { t } = useTranslation("recordings");
	const navigate = useNavigate();
	const utils = api.useUtils();
	const recorder = useRecorderHandle();
	const [continuing, setContinuing] = useState(false);

	const finalize = api.recordings.finalize.useMutation({
		onSuccess: () => {
			toast.success(t("detail.stalled.finalizeToast"));
			// Сброс глобального recorder-стора, если финализируем именно ту
			// активную запись (иначе индикатор записи в шапке «зависает»).
			if (useRecorderStore.getState().recordingId === recording.id) {
				useRecorderStore.getState().reset();
			}
			void utils.recordings.getById.invalidate({ recordingId: recording.id });
		},
		onError: (err) => {
			toastError(err, t("detail.stalled.finalizeError"), t);
		},
	});

	const reprocess = api.recordings.reprocess.useMutation({
		onSuccess: () => {
			toast.success(t("detail.stalled.reprocessToast"));
			void utils.recordings.getById.invalidate({ recordingId: recording.id });
		},
		onError: (err) => {
			toastError(err, t("detail.stalled.reprocessError"), t);
		},
	});

	const remove = api.recordings.deleteRecording.useMutation({
		onSuccess: () => {
			toast.success(t("detail.stalled.deleteToast"));
			if (useRecorderStore.getState().recordingId === recording.id) {
				useRecorderStore.getState().reset();
			}
			void navigate({ to: "/recordings" });
		},
		onError: (err) => {
			toastError(err, t("detail.stalled.deleteError"), t);
		},
	});

	const handleContinue = useCallback(async () => {
		setContinuing(true);
		try {
			const fromChunks = recording.chunksUploaded * CHUNK_SEC * 1000;
			const fromDuration = recording.durationSec * 1000;
			await recorder.continueExisting({
				recordingId: recording.id,
				chunksUploaded: recording.chunksUploaded,
				elapsedMs: Math.max(fromChunks, fromDuration),
				inputDeviceId: readPreferredMic() ?? undefined,
			});
			if (useRecorderStore.getState().status === "error") {
				toast.error(t("detail.stalled.continueError"), {
					description: useRecorderStore.getState().error ?? undefined,
				});
				return;
			}
			toast.success(t("detail.stalled.continueToast"));
			// Остаёмся на /recordings/$id — RecordingLiveScreen подхватит
			// isLocallyActive и покажет live-overlay (URL уже с id).
		} catch (err) {
			toastError(err, t("detail.stalled.continueError"), t);
		} finally {
			setContinuing(false);
		}
	}, [recorder, recording, t]);

	const isFailed = mode === "failed";
	const busy = finalize.isPending || remove.isPending || reprocess.isPending || continuing;

	return (
		<>
			<DetailHeader title={recording.title} />

			<ElevatedPanel
				elevation="e1"
				className={`space-y-4 p-5 ${isFailed ? "border-destructive/25" : "border-warning/30"}`}
			>
				<div className="flex items-start gap-3">
					<span
						className={`grid size-10 shrink-0 place-items-center rounded-[11px] [box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)] ${
							isFailed ? "bg-destructive/10 text-destructive" : "bg-warning/15 text-warning"
						}`}
					>
						<IconAlertTriangle size={20} />
					</span>
					<div className="min-w-0 flex-1">
						<div className="font-semibold text-foreground">
							{isFailed ? t("detail.stalled.failedTitle") : t("detail.stalled.title")}
						</div>
						<p className="mt-1 text-sm text-muted-foreground">
							{isFailed ? t("detail.stalled.failedDescription") : t("detail.stalled.description")}
						</p>
					</div>
				</div>
				{/* На mobile primary→secondary стопкой full-width, деструктивную
				    «Удалить» отделяем (mt-2 + граница), чтобы не нажать случайно. */}
				<div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
					{isFailed && (
						<Button
							className="cursor-pointer max-sm:w-full pointer-coarse:h-11"
							disabled={busy}
							onClick={() => reprocess.mutate({ recordingId: recording.id })}
						>
							{reprocess.isPending ? (
								<IconLoader2 size={16} className="mr-2 animate-spin" />
							) : (
								<IconRefresh size={16} className="mr-2" />
							)}
							{reprocess.isPending
								? t("detail.stalled.reprocessing")
								: t("detail.stalled.reprocess")}
						</Button>
					)}
					{!isFailed && (
						<>
							<Button
								className="cursor-pointer max-sm:w-full pointer-coarse:h-11"
								disabled={busy}
								onClick={() => void handleContinue()}
							>
								{continuing ? (
									<IconLoader2 size={16} className="mr-2 animate-spin" />
								) : (
									<IconPlayerPlay size={16} className="mr-2" />
								)}
								{continuing ? t("detail.stalled.continuing") : t("detail.stalled.continue")}
							</Button>
							<Button
								variant="outline"
								className="cursor-pointer max-sm:w-full pointer-coarse:h-11"
								disabled={busy}
								onClick={() =>
									finalize.mutate({
										recordingId: recording.id,
										expectedChunks: recording.chunksUploaded,
									})
								}
							>
								{finalize.isPending ? (
									<IconLoader2 size={16} className="mr-2 animate-spin" />
								) : (
									<IconPlayerStopFilled size={16} className="mr-2" />
								)}
								{finalize.isPending ? t("detail.stalled.finalizing") : t("detail.stalled.finalize")}
							</Button>
						</>
					)}
					<Button
						variant="outline"
						className="cursor-pointer text-destructive max-sm:mt-2 max-sm:w-full max-sm:border-t max-sm:border-border max-sm:pt-4 pointer-coarse:h-11 sm:ml-auto"
						disabled={busy}
						onClick={() => remove.mutate({ recordingId: recording.id })}
					>
						<IconTrash size={16} className="mr-2" />
						{t("detail.stalled.delete")}
					</Button>
				</div>
			</ElevatedPanel>

			{transcript.length > 0 && (
				<section className="space-y-3">
					<h2 className="text-base font-semibold text-foreground">{t("detail.transcriptTitle")}</h2>
					<ul className="space-y-2">
						{transcript.map((seg, idx) => (
							<li
								key={`${seg.speakerId}-${seg.startSec}-${idx}`}
								className="rounded-[var(--radius-card)] border border-border bg-card p-3.5 shadow-e1"
							>
								<div className="mb-1.5 flex items-center gap-2 text-xs font-medium text-muted-foreground">
									<TimecodeChip sec={seg.startSec} />
								</div>
								<div className="text-sm leading-relaxed text-foreground">
									<Suspense fallback={<p className="text-sm">{seg.text}</p>}>
										<MarkdownLatex source={seg.text} className="text-sm" />
									</Suspense>
								</div>
							</li>
						))}
					</ul>
				</section>
			)}
		</>
	);
}

interface RecordingSearch {
	/** Seek-по-таймкоду (сек) — переход из citation тьютора (lecture-timestamp). */
	t?: number;
}

export const Route = createFileRoute("/recordings/$id")({
	component: RecordingDetailPage,
	pendingComponent: PagePendingFallback,
	validateSearch: (search: Record<string, unknown>): RecordingSearch => {
		const raw = search.t;
		const t = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : Number.NaN;
		return Number.isFinite(t) && t >= 0 ? { t } : {};
	},
});

function RecordingDetailPage(): React.ReactElement {
	const { t } = useTranslation("recordings");
	const { id } = useParams({ from: "/recordings/$id" });
	useArtifactWatching(markArtifactSeenKey("recording", id));
	const utils = api.useUtils();
	const { t: seekParam } = useSearch({ from: "/recordings/$id" });
	const query = useRecording(id);
	const recording = query.data;
	const transcript: TranscriptSegmentDto[] = useMemo(
		() => recording?.transcript ?? [],
		[recording],
	);
	const isProcessing = recording?.status === "processing" || recording?.status === "uploading";
	// Прерванная (refresh/краш во время записи) или неудавшаяся запись — плеер
	// показывать нельзя (нет аудио, duration=0). Вместо него — StalledRecordingView.
	const isStalled = recording?.status === "recording" || recording?.status === "paused";
	const isFailed = recording?.status === "error";

	// Активная фоновая запись на ЭТОМ устройстве (движок жив в RecorderProvider):
	// открытие её страницы не должно показывать «Запись прервана» — уводим на
	// полноэкранный live-экран записи.
	const activeRecordingId = useRecorderStore((s) => s.recordingId);
	const recorderStatus = useRecorderStore((s) => s.status);
	const isLocallyActive =
		activeRecordingId === id &&
		(recorderStatus === "recording" ||
			recorderStatus === "paused" ||
			recorderStatus === "starting" ||
			recorderStatus === "stopping");

	// Surface-ack: запись этого id обработана и пользователь на её странице
	// (видим) → гасим email/push + колокол (presence-aware §3.3, матрица 4.1).
	// scope по recordingId из payload === route id; ack на любом терминальном
	// состоянии, которое пользователь видит на странице — ready ИЛИ partial.
	// processRecording шлёт email (RECORDING_PROCESSED) и при partial, поэтому
	// ack только на "ready" пропускал бы письмо для видимого partial-результата.
	const markRecordingSeen = useMarkArtifactSeen();
	useSocketEvent("recording:processed", (payload) => {
		if (payload.recordingId !== id) return;
		void utils.recordings.getById.invalidate({ recordingId: id });
		if (payload.status === "ready" || payload.status === "partial") {
			markRecordingSeen(markArtifactSeenKey("recording", id), "surface");
		}
	});

	// Socket recording:processed — best-effort; без poll loader залипает в processing.
	const refetchRecordingRef = useRef(query.refetch);
	useEffect(() => {
		refetchRecordingRef.current = query.refetch;
	}, [query.refetch]);
	useEffect(() => {
		if (!isProcessing) return;
		const timer = setInterval(() => void refetchRecordingRef.current(), 3_000);
		return () => clearInterval(timer);
	}, [isProcessing]);

	// Presigned playback URL — refetch при истечении (TTL 10 min на бэкенде).
	const playbackQuery = api.recordings.getPlaybackUrl.useQuery(
		{ recordingId: id },
		{
			enabled: Boolean(recording?.id) && !isProcessing && !isStalled && !isFailed,
			refetchInterval: 9 * 60 * 1000,
			retry: 1,
		},
	);

	const audioRef = useRef<HTMLAudioElement | null>(null);
	const [position, setPosition] = useState(0);
	const [isPlaying, setIsPlaying] = useState(false);
	// Режим правой колонки: структурированный «Документ» (live-конспект) или
	// голый «Транскрипт». Default — «Документ».
	const [contentView, setContentView] = useState<"doc" | "transcript">(() => {
		try {
			return localStorage.getItem("studyhub-recording-detailview") === "transcript"
				? "transcript"
				: "doc";
		} catch {
			return "doc";
		}
	});
	const changeContentView = (v: "doc" | "transcript"): void => {
		setContentView(v);
		try {
			localStorage.setItem("studyhub-recording-detailview", v);
		} catch {
			/* ignore */
		}
	};

	const togglePlay = useCallback((): void => {
		const audio = audioRef.current;
		if (!audio) return;
		if (audio.paused) {
			void startPlayback(audio);
		} else {
			audio.pause();
		}
	}, []);

	// Отложенный seek по таймкоду. Корень «дёрганья без перезагрузки»: после
	// перехода processing→ready <audio> только что смонтировался и его metadata
	// ещё не загружена (readyState 0, duration=NaN). Присвоение currentTime в этот
	// момент браузер игнорирует → ползунок прыгал и тут же откатывался. Если
	// metadata не готова — кладём цель в pendingSeekRef и применяем её в
	// onLoadedMetadata (тот же приём, что и для ?t=-seek). Чип остаётся
	// кликабельным — ранний клик «доедет» к точке, как только аудио будет готово.
	const pendingSeekRef = useRef<number | null>(null);

	const seekTo = useCallback((sec: number): void => {
		const audio = audioRef.current;
		if (!audio || audio.readyState < 1) {
			// Аудио не смонтировано / metadata не загружена — откладываем seek.
			pendingSeekRef.current = sec;
			setPosition(sec);
			return;
		}
		pendingSeekRef.current = null;
		const target = Math.max(0, Math.min(audio.duration || sec, sec));
		audio.currentTime = target;
		setPosition(target);
	}, []);

	const seekBy = useCallback(
		(delta: number): void => {
			const audio = audioRef.current;
			if (!audio) return;
			const next = Math.max(0, Math.min(audio.duration || 0, audio.currentTime + delta));
			seekTo(next);
		},
		[seekTo],
	);

	// Применить ?t=NNN один раз на каждое новое значение: перемотать + автоплей.
	// metadata аудио грузится асинхронно — пробуем сразу (если уже readyState>=1)
	// и повторяем из onLoadedMetadata (handleLoadedMetadata) пока флаг не закрыт.
	const appliedSeekRef = useRef<number | null>(null);

	const applyPendingSeek = useCallback((): void => {
		if (seekParam == null) return;
		if (appliedSeekRef.current === seekParam) return;
		const audio = audioRef.current;
		// readyState < 1 (HAVE_METADATA) → duration ещё неизвестна, ждём metadata.
		if (!audio || audio.readyState < 1) return;
		appliedSeekRef.current = seekParam;
		const target = Math.max(0, Math.min(audio.duration || seekParam, seekParam));
		audio.currentTime = target;
		setPosition(target);
		// Автоплей мог быть заблокирован браузером (no user gesture) — позиция
		// уже выставлена, пользователь нажмёт play вручную.
		void startPlayback(audio);
	}, [seekParam]);

	// onLoadedMetadata: аудио готово — применяем и ?t=-seek (citation), и
	// отложенный пользовательский seek по таймкоду (клик до готовности metadata).
	const handleLoadedMetadata = useCallback((): void => {
		applyPendingSeek();
		const audio = audioRef.current;
		const pending = pendingSeekRef.current;
		if (audio && pending != null) {
			pendingSeekRef.current = null;
			const target = Math.max(0, Math.min(audio.duration || pending, pending));
			audio.currentTime = target;
			setPosition(target);
		}
	}, [applyPendingSeek]);

	// При смене seekParam (новый переход из citation) — сбрасываем флаг и пробуем.
	useEffect(() => {
		appliedSeekRef.current = null;
		applyPendingSeek();
	}, [applyPendingSeek]);

	const duration = recording?.durationSec ?? 0;
	const hasAudio = Boolean(playbackQuery.data?.url);

	// Live-запись на этом устройстве — полноэкранный overlay (URL уже /recordings/$id).
	if (isLocallyActive) {
		return <RecordingLiveScreen />;
	}

	return (
		<div className="space-y-5 px-4 pt-3 pb-6 lg:px-6 lg:pb-0">
			<Button variant="ghost" size="sm" asChild className="cursor-pointer pointer-coarse:h-11">
				<Link to="/recordings">
					<IconArrowLeft size={16} className="mr-1.5" />
					{t("detail.allRecordings")}
				</Link>
			</Button>

			{query.isLoading ? (
				<div className="space-y-3" aria-busy>
					<Skeleton className="h-9 w-2/3 rounded-[var(--radius-card)]" />
					<Skeleton className="h-36 w-full rounded-[var(--radius-panel)]" />
					<Skeleton className="h-64 w-full rounded-[var(--radius-panel)]" />
				</div>
			) : query.isError ? (
				<ErrorState
					title={t("detail.loadError")}
					action={
						<Button onClick={() => void query.refetch()} className="cursor-pointer">
							{t("detail.retry")}
						</Button>
					}
				/>
			) : query.isNotFound || !recording ? (
				<ErrorState
					title={t("detail.notFoundTitle")}
					description={t("detail.notFoundDescription")}
					action={
						<Button asChild className="cursor-pointer">
							<Link to="/recordings">{t("detail.allRecordings")}</Link>
						</Button>
					}
				/>
			) : isProcessing ? (
				<>
					<DetailHeader title={recording.title} />
					<ProcessingLoader recordingId={recording.id} stage="transcribing" />
				</>
			) : isStalled || isFailed ? (
				<StalledRecordingView
					recording={recording}
					transcript={transcript}
					mode={isFailed ? "failed" : "stalled"}
				/>
			) : (
				/* Desktop: bounded fit-to-screen грид. Плеер сверху-слева (self-start),
				    карточка конспекта прижата к низу левого столбца (self-end),
				    в правой колонке скроллится только контент (заголовок+табы fixed).
				    На mobile — flex-col со скроллом страницы; заголовок+табы sticky
				    под topbar (order-1/2/3). */
				<div className="flex flex-col gap-5 lg:grid lg:h-[calc(100dvh-var(--topbar-height)-var(--audio-player-height,0px)-4rem)] lg:overflow-hidden lg:[grid-template-columns:minmax(0,0.92fr)_minmax(0,1.08fr)] lg:[grid-template-rows:auto_minmax(0,1fr)]">
					{/* ── ЛЕВО: плеер + спикеры (натуральная высота, верх столбца) ── */}
					<div className="order-1 space-y-4 lg:col-start-1 lg:row-start-1 lg:self-start">
						{/* Шапка + меню «Скачать» (транскрипт .txt / конспект .md / аудио .mp3).
						    На mobile actions сверху, заголовок во всю ширину ниже.
						    wrapActions: в узкой колонке (открыта панель репетитора) меню
						    уезжает под заголовок, а не сплющивает его в столбик слов. */}
						<EntityDetailHeader
							wrapActions
							title={
								<DetailHeader
									title={recording.title}
									meta={[
										<span key="dur">{formatDurationHms(duration)}</span>,
										transcript.length > 0 ? (
											<span key="cnt">
												{t("detail.transcriptCount", { count: transcript.length })}
											</span>
										) : null,
									]}
								/>
							}
							actions={<RecordingExportMenu recordingId={recording.id} title={recording.title} />}
						/>

						{/* Тактильный аудиоплеер — e2-панель */}
						<ElevatedPanel className={cn(PANEL_PAD_CHROME, "space-y-4")}>
							{hasAudio && (
								<audio
									ref={audioRef}
									src={playbackQuery.data?.url}
									preload="metadata"
									className="hidden"
									onLoadedMetadata={handleLoadedMetadata}
									onPlay={() => setIsPlaying(true)}
									onPause={() => setIsPlaying(false)}
									onEnded={() => setIsPlaying(false)}
									onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
								>
									<track kind="captions" />
								</audio>
							)}
							<div className="flex items-center gap-4">
								{/* Транспорт-клавиши: -15с / play / +15с */}
								<button
									type="button"
									onClick={() => seekBy(-15)}
									disabled={!hasAudio}
									className="sh-tactile-key-neutral grid size-11 place-items-center rounded-[var(--radius-lg)] border border-border text-foreground [background-image:linear-gradient(180deg,var(--surface),var(--surface-2))] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
									aria-label={t("detail.player.rewind")}
								>
									<IconPlayerTrackPrevFilled size={16} />
								</button>
								<button
									type="button"
									onClick={togglePlay}
									disabled={!hasAudio}
									className="sh-tactile-key grid size-16 place-items-center rounded-full border-0 text-[var(--sh-primary-fg)] [background-image:linear-gradient(180deg,var(--primary-2),var(--primary))] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
									aria-label={isPlaying ? t("detail.player.pause") : t("detail.player.play")}
								>
									{isPlaying ? (
										<IconPlayerPauseFilled size={26} />
									) : (
										<IconPlayerPlayFilled size={26} />
									)}
								</button>
								<button
									type="button"
									onClick={() => seekBy(15)}
									disabled={!hasAudio}
									className="sh-tactile-key-neutral grid size-11 place-items-center rounded-[var(--radius-lg)] border border-border text-foreground [background-image:linear-gradient(180deg,var(--surface),var(--surface-2))] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
									aria-label={t("detail.player.forward")}
								>
									<IconPlayerTrackNextFilled size={16} />
								</button>
								<div className="flex-1 text-right text-sm tabular-nums text-muted-foreground">
									<span className="font-semibold text-foreground">
										{formatDurationHms(position)}
									</span>
									<span className="mx-1">/</span>
									<span>{formatDurationHms(duration)}</span>
								</div>
							</div>
							{/* Утопленный well-scrubber. На touch — выше hit-зона (py-3),
							    чтобы попасть пальцем по таймлайну длинной лекции. */}
							<div className="rounded-full bg-[var(--well)] px-1 py-1 pointer-coarse:py-3 [box-shadow:inset_0_1px_3px_rgb(var(--shadow)/0.13)]">
								<Slider
									value={[position]}
									max={duration || 1}
									step={1}
									disabled={!hasAudio}
									onValueChange={(v) => seekTo(v[0] ?? 0)}
									aria-label={t("detail.player.title")}
								/>
							</div>
							{playbackQuery.isError && (
								<p className="text-xs text-destructive">{t("detail.player.unavailable")}</p>
							)}
						</ElevatedPanel>
					</div>

					{/* ── ПРАВО: транскрипт. На mobile сразу под плеером (order-2),
					    на desktop — правая колонка на обе строки.
					    Заголовок + табы: mobile sticky под topbar, desktop — сверху
					    колонки; скроллится только контент ниже. ── */}
					<div className="order-2 flex flex-col gap-3 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:min-h-0 lg:overflow-hidden lg:pr-1">
						<div className="sticky top-[var(--topbar-height)] z-20 -mx-4 space-y-3 bg-background/95 px-4 py-2 backdrop-blur-sm lg:static lg:z-auto lg:mx-0 lg:shrink-0 lg:bg-transparent lg:px-0 lg:py-0 lg:backdrop-blur-none">
							<h2 className="text-base font-semibold text-foreground">
								{contentView === "doc" ? t("doc.docTitle") : t("detail.transcriptTitle")}
							</h2>
							<SegmentedTabs
								value={contentView}
								onChange={changeContentView}
								options={[
									{
										value: "doc",
										label: t("doc.tabDoc"),
										icon: <IconFileText size={15} />,
									},
									{
										value: "transcript",
										label: t("doc.tabTranscript"),
										icon: <IconWaveSine size={15} />,
									},
								]}
							/>
						</div>
						<div className="min-w-0 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain">
							{contentView === "doc" ? (
								<RecordingDocView
									mode="review"
									recordingId={recording.id}
									onSeek={seekTo}
									canSeek={hasAudio}
								/>
							) : transcript.length === 0 ? (
								<p className="rounded-[var(--radius-card)] border border-border bg-card px-4 py-6 text-center text-sm italic text-muted-foreground">
									{t("detail.transcriptEmpty")}
								</p>
							) : (
								<ul className="space-y-2">
									{transcript.map((seg, idx) => {
										return (
											<li
												key={`${seg.startSec}-${idx}`}
												className="rounded-[var(--radius-card)] border border-border bg-card p-3.5 shadow-e1"
											>
												<div className="mb-1.5 flex items-center gap-2">
													<TimecodeChip
														sec={seg.startSec}
														onSeek={seekTo}
														canSeek={hasAudio}
														jumpLabel={(time) => t("detail.jumpToSegment", { time })}
													/>
												</div>
												<div className="text-sm leading-relaxed text-foreground">
													<Suspense fallback={<p className="text-sm">{seg.text}</p>}>
														<MarkdownLatex source={seg.text} className="text-sm" />
													</Suspense>
												</div>
											</li>
										);
									})}
								</ul>
							)}
						</div>
					</div>

					{/* Конспект — на mobile под транскриптом (order-3), на desktop
					    в левой колонке под плеером (row-2). Нижний отступ, чтобы
					    карточка не липла к краю экрана (desktop fit-to-screen грид
					    с self-end + safe-area на mobile). */}
					<div className="order-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] lg:col-start-1 lg:row-start-2 lg:self-end lg:pb-3">
						<RecordingNoteCard
							recordingId={recording.id}
							savedMaterialId={recording.savedMaterialId}
							canGenerate={recording.status === "ready" || recording.status === "partial"}
						/>
					</div>
				</div>
			)}
		</div>
	);
}
